// 002 y 003 — Anamnesis / Examen físico (Fase 7, Tanda 17, ver PLAN.md). Transcripción del PDF oficial del Anexo 1 MSP: 002
// hojas idx 9-10 y 003 hojas idx 14-15. Informe de brechas en `docs/hce-fase7/brechas-002-003-005-006-007.md`. Este módulo
// tiene lo COMÚN a las dos anamnesis (revisión de órganos, examen físico, diagnósticos, casillas de antecedentes) y los
// builders de los recursos FHIR; lo propio de cada una está en `bloques002Datos.ts` y `bloques003Datos.ts`.
//
// Decisiones (Sue, 2026-09-18): la anamnesis completada se marca con una `Composition` (type
// `http://logiacare.ec/fhir/formularios-msp|002|003`) que las 3 verificaciones de completitud aceptan igual que el
// `QuestionnaireResponse` histórico; en 002 se pierde la creación de `AllergyIntolerance` (el papel no trae campo de
// alergias; sí en 003); cada diagnóstico con texto crea un `Condition` sin `verificationStatus` (PRE/DEF solo en el PDF).
// Del papel: en C/D/G/H cada ítem es una casilla y cada bloque tiene su "Describa" (número + descripción). La silueta
// corporal de 003 no se transcribe (equivalencia funcional, como 058/115).
import type { CodeableConcept } from '@medplum/fhirtypes';
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { MARCAS_PRE_DEF, paresDeCampos, type CampoSimple, type Valores } from './camposSimplesDatos';
import { codeDeDiagnostico } from './SelectorDiagnosticoCie10';
import type { Par } from './index';

export const SISTEMA_FORMULARIOS_MSP = 'http://logiacare.ec/fhir/formularios-msp';
export const NUM_DIAGNOSTICOS_ANAMNESIS = 6;

const casilla = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'casilla' });
const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });
/** Casillas numeradas como en el papel más su "Describa" (registrar el número y una breve descripción). */
export const casillasConDescribe = (prefijo: string, etiquetas: readonly string[], describa = 'Describa', sufijoNumero = ''): readonly CampoSimple[] => [
  ...etiquetas.map((e, i) => casilla(`${prefijo}${i + 1}`, `${i + 1}${sufijoNumero}. ${e}`)),
  area(`${prefijo}Describa`, describa),
];

/** Antecedentes patológicos familiares (D en 002, D en 003) y personales de 002 (C): las mismas 10 categorías. */
export const CATEGORIAS_ANTECEDENTES = ['Cardiopatía', 'Hipertensión', 'Enf. C. Vascular', 'Endócrino Metabólico', 'Cáncer', 'Tuberculosis', 'Enf. Mental', 'Enf. Infecciosa', 'Mal Formación', 'Otro'] as const;

export const CAMPOS_FAMILIARES_ANAMNESIS = casillasConDescribe('fa', CATEGORIAS_ANTECEDENTES);
export const CAMPOS_ENFERMEDAD_ANAMNESIS: readonly CampoSimple[] = [area('enfermedad', 'Enfermedad o problema actual')];

export const CAMPOS_REVISION_ANAMNESIS = casillasConDescribe('re', [
  'Piel - Anexos', 'Órganos de los sentidos', 'Respiratorio', 'Cardio - vascular', 'Digestivo', 'Genito - urinario', 'Músculo - esquelético', 'Endocrino', 'Hemo - linfático', 'Nervioso',
]);

export const CAMPOS_EXAMEN_REGIONAL = casillasConDescribe(
  'er',
  ['Piel - Faneras', 'Cabeza', 'Ojos', 'Oídos', 'Nariz', 'Boca', 'Orofaringe', 'Cuello', 'Axilas - Mamas', 'Tórax', 'Abdomen', 'Columna vertebral', 'Ingle - Periné', 'Miembros superiores', 'Miembros inferiores'],
  'Examen físico regional — describa',
  'R',
);
export const CAMPOS_EXAMEN_SISTEMICO = casillasConDescribe(
  'es',
  ['Órganos de los sentidos', 'Respiratorio', 'Cardio - vascular', 'Digestivo', 'Genital', 'Urinario', 'Músculo - esquelético', 'Endócrino', 'Hemo - linfático', 'Neurológico'],
  'Examen físico sistémico — describa',
  'S',
);

export const CAMPOS_PLAN_ANAMNESIS: readonly CampoSimple[] = [area('plan', 'Plan de tratamiento')];

export function diagnosticosVacios(): readonly DiagnosticoConMarca[] {
  return Array.from({ length: NUM_DIAGNOSTICOS_ANAMNESIS }, () => diagnosticoConMarcaVacio());
}

export function paresDiagnosticos(dx: readonly DiagnosticoConMarca[]): Par[] {
  return dx.map((d, i) => ({
    pregunta: `Diagnóstico ${i + 1}`,
    respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
  }));
}

/** Un `CodeableConcept` por diagnóstico con texto (para los `Condition`). */
export const codigosDiagnostico = (dx: readonly DiagnosticoConMarca[]): CodeableConcept[] => dx.filter((d) => d.diagnostico.texto.trim()).map((d) => codeDeDiagnostico(d.diagnostico));

/** Texto de "Hallazgos de examen físico": lo marcado (número + rótulo) y las descripciones, regional y sistémico. */
export function hallazgosExamenFisico(regional: Valores, sistemico: Valores): string {
  const bloque = (titulo: string, campos: readonly CampoSimple[], v: Valores): string => {
    const marcados = campos.filter((c) => c.tipo === 'casilla' && v[c.clave] === 'true').map((c) => c.etiqueta);
    const descripcion = (v[campos.find((c) => c.tipo === 'area')!.clave] ?? '').trim();
    return [marcados.length ? `${titulo}: ${marcados.join(', ')}` : '', descripcion].filter(Boolean).join('. ');
  };
  return [bloque('Regional', CAMPOS_EXAMEN_REGIONAL, regional), bloque('Sistémico', CAMPOS_EXAMEN_SISTEMICO, sistemico)].filter(Boolean).join('\n');
}

export const paresBloque = (campos: readonly CampoSimple[], valores: Valores, prefijo: string): Par[] => paresDeCampos(campos, valores, prefijo);
